import { Box, Container, Divider, Grid, Paper, Stack } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { PluginsForm } from '../plugins-form'
import { useTranslation } from 'react-i18next'
import { SectionHeader } from '@sitoo/mui-components'
import { TextFieldInput } from '../../../../inputs/text-field-input'
import { useQuery, useMutation } from '@apollo/client'
import {
  RecognitionCrmSettings,
  RecognitionPosSettings,
  RecognitionPosSettingsDocument,
  UpdateRecognitionPosSettingsDocument,
  RecognitionLookupFieldName,
  UpdateRecognitionCrmSettingsDocument,
  RecognitionRequiredFieldName,
  RecognitionUpdateFieldName,
  RecognitionChannelsDocument,
} from '../../../../generated/graphql'
import { useEffect, useMemo } from 'react'
import { DialogInput } from '../../../../inputs/dialog-input'
import { useNavigate, useParams } from 'react-router-dom'
import { ConsentsInput } from './consents-input'
import { ContactPreferencesInput } from './contact-preferences-input'
import { useAbsolutePath } from '../../../../hooks/absolute-path'
import { RootRoute } from '../../..'

type Form = RecognitionPosSettings & RecognitionCrmSettings

export const SettingsReconitionPage = () => {
  const { t } = useTranslation(['apps', 'shared'])

  const navigate = useNavigate()
  const generatePath = useAbsolutePath()

  const { id: channelId } = useParams<{ id: string }>()

  const formContext = useForm<Form>({
    defaultValues: {
      preboarding: {
        boarding_url: '',
        terminal_header_text: '',
        terminal_footer_text: '',
      },
      authorization_fields: [],
      consents: [],
      contact_preferences: [],
      lookup_fields: [],
      required_fields: [],
      update_fields: [],
    },
  })
  const { control } = formContext

  const { data: channelsData, loading: channelsLoading } = useQuery(
    RecognitionChannelsDocument,
  )

  // Redirect back to the list if the channel does not exist
  useEffect(() => {
    if (channelsLoading) return

    const isExistingChannel = channelsData?.settings.crm?.channels.some(
      (channel) => channel.id === channelId,
    )

    if (!isExistingChannel) {
      void navigate(generatePath(RootRoute.AppsRecognitions))
    }
  }, [channelId, channelsData, channelsLoading, generatePath, navigate])

  const { data, loading: fetchLoading } = useQuery(
    RecognitionPosSettingsDocument,
    {
      variables: { channelId: String(channelId) },
      fetchPolicy: 'cache-and-network',
    },
  )

  const [updatePosSettings, { loading: updatePosLoading }] = useMutation(
    UpdateRecognitionPosSettingsDocument,
  )

  const [updateCrmSettings, { loading: updateCrmLoading }] = useMutation(
    UpdateRecognitionCrmSettingsDocument,
  )

  const isLoading =
    fetchLoading || updatePosLoading || updateCrmLoading || channelsLoading

  const onSubmit = async (formData: Form) => {
    const { preboarding, crm_channel_id, ...crmSettings } = formData

    const { data: posData } = await updatePosSettings({
      variables: {
        channelId: String(channelId),
        settings: preboarding,
      },
    })

    const { data: crmData } = await updateCrmSettings({
      variables: {
        channelId: String(channelId),
        settings: crmSettings,
      },
    })

    return {
      ...posData?.updateRecognitionPosSettings,
      ...crmData?.updateRecognitionCrmSettings,
    }
  }

  const serverData = useMemo(
    () => ({
      ...data?.recognitionPosSettings,
      ...data?.recognitionCrmSettings,
    }),
    [data?.recognitionPosSettings, data?.recognitionCrmSettings],
  )

  const formatLookupOption = (value: string) => {
    return {
      label: t(`apps:recognition.lookup_field.${value}`),
      value,
    }
  }

  const formatRequiredOption = (value: string) => {
    return {
      label: t(`apps:recognition.required_field.${value}`),
      value,
    }
  }

  const formatUpdateOption = (value: string) => {
    return {
      label: t(`apps:recognition.update_field.${value}`),
      value,
    }
  }

  return (
    <Container>
      <FormProvider {...formContext}>
        <PluginsForm
          serverData={serverData}
          onSubmit={onSubmit}
          submitLabel={t('shared:action.save')}
          isLoading={isLoading}
        >
          <Paper sx={{ mb: 2 }}>
            <SectionHeader variant="transparent">
              {t('apps:recognition.prefilled_boarding_section')}
            </SectionHeader>

            <Grid container sx={{ pb: 2 }}>
              <Grid size={{ md: 6, xs: 12 }}>
                <Stack gap={2} sx={{ p: 2, pt: 1 }}>
                  <TextFieldInput
                    name="preboarding.terminal_header_text"
                    control={control}
                    label={t('apps:recognition.terminal_header_text_label')}
                    fullWidth
                    required
                  />

                  <TextFieldInput
                    name="preboarding.terminal_footer_text"
                    control={control}
                    label={t('apps:recognition.terminal_footer_text_label')}
                    fullWidth
                    required
                  />

                  <TextFieldInput
                    name="preboarding.boarding_url"
                    control={control}
                    label={t('apps:recognition.boarding_url_label')}
                    fullWidth
                    required
                  />
                </Stack>
              </Grid>

              <Grid
                size={{ md: 6 }}
                sx={{ display: { xs: 'none', md: 'block' }, px: 2 }}
              >
                <Box
                  sx={{
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    justifyContent: 'center',
                    alignItems: 'center',
                    display: 'grid',
                    padding: 3,
                  }}
                >
                  <Box
                    sx={{
                      width: '161px',
                      height: '266px',
                      backgroundImage: 'url(/terminal.svg)',
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <Paper>
            <SectionHeader variant="transparent">
              {t('apps:recognition.web_boarding_section')}
            </SectionHeader>

            <DialogInput
              name="authorization_fields"
              control={control}
              itemLabel={t(`apps:recognition.authorization_fields_label`)}
              addLabel={t('apps:recognition.authorization_fields_add_label')}
              formatValue={formatLookupOption}
              options={[
                RecognitionLookupFieldName.Email,
                RecognitionLookupFieldName.Mobile,
              ]}
              emptyMessage={t(`apps:recognition.fields_empty_state`)}
              isLoading={isLoading}
              isDraggable={false}
              transform={{
                input: (values) => values.map((value) => value.name),
                output: (values) => values.map((value) => ({ name: value })),
              }}
            />

            <DialogInput
              name="lookup_fields"
              control={control}
              itemLabel={t(`apps:recognition.lookup_fields_label`)}
              addLabel={t('apps:recognition.lookup_fields_add_label')}
              formatValue={formatLookupOption}
              options={[
                RecognitionLookupFieldName.Email,
                RecognitionLookupFieldName.Mobile,
              ]}
              emptyMessage={t(`apps:recognition.fields_empty_state`)}
              isLoading={isLoading}
              isDraggable={false}
              transform={{
                input: (values) => values.map((value) => value.name),
                output: (values) => values.map((value) => ({ name: value })),
              }}
            />

            <DialogInput
              name="required_fields"
              control={control}
              itemLabel={t(`apps:recognition.required_fields_label`)}
              addLabel={t('apps:recognition.required_fields_add_label')}
              formatValue={formatRequiredOption}
              options={[
                RecognitionRequiredFieldName.Email,
                RecognitionRequiredFieldName.Mobile,
                RecognitionRequiredFieldName.FirstName,
                RecognitionRequiredFieldName.LastName,
              ]}
              emptyMessage={t(`apps:recognition.fields_empty_state`)}
              isLoading={isLoading}
              isDraggable={false}
              transform={{
                input: (values) => values.map((value) => value.name),
                output: (values) => values.map((value) => ({ name: value })),
              }}
            />

            <DialogInput
              name="update_fields"
              control={control}
              itemLabel={t(`apps:recognition.update_fields_label`)}
              addLabel={t('apps:recognition.update_fields_add_label')}
              formatValue={formatUpdateOption}
              options={[
                RecognitionUpdateFieldName.Email,
                RecognitionUpdateFieldName.Mobile,
                RecognitionUpdateFieldName.FirstName,
                RecognitionUpdateFieldName.LastName,
                RecognitionUpdateFieldName.Consents,
                RecognitionUpdateFieldName.ContactPreferences,
                RecognitionUpdateFieldName.Recognition,
              ]}
              emptyMessage={t(`apps:recognition.fields_empty_state`)}
              isLoading={isLoading}
              isDraggable={false}
              transform={{
                input: (values) => values.map((value) => value.name),
                output: (values) => values.map((value) => ({ name: value })),
              }}
            />

            <ConsentsInput name="consents" control={control} />

            <Divider />

            <ContactPreferencesInput
              name="contact_preferences"
              control={control}
            />
          </Paper>
        </PluginsForm>
      </FormProvider>
    </Container>
  )
}
